import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classnames from 'classnames';
import { useState } from 'react';
import styled from 'styled-components';

import { ShopItemType } from '@sorare/core/src/__generated__/globalTypes';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { ClubShopItemFooter } from '@sorare/core/src/components/clubShop/ClubShopItem/ClubShopItemFooter';
import { ClubShopItemStatus } from '@sorare/core/src/components/clubShop/ClubShopItemStatus';

import { Item } from 'components/shopItems/ShopItemPicker/Item';
import { ItemPreviewDialog } from 'components/shopItems/ShopItemPicker/ItemPreviewDialog';
import { useItemExpired } from 'components/shopItems/ShopItemPicker/Listings/useItemExpired';

import type {
  DefaultListing_shopItem,
  DefaultListing_userSportProfile,
} from './__generated__/index.graphql';

const FooterWrapper = styled.div`
  grid-area: footer;
  position: relative;
`;
const ImageContainer = styled.div`
  grid-area: image;
  border-radius: var(--half-unit);
  overflow: hidden;

  &.background {
    grid-area: none;
    position: absolute;
    height: 100%;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &:after {
      content: '';
      position: absolute;
      inset: 0;
      background-image: linear-gradient(
        rgba(var(--c-rgb-black), 0.4) 0%,
        transparent 40%,
        transparent 60%,
        rgba(var(--c-rgb-black), 0.4) 100%
      );
    }
  }
`;
const ItemImage = styled(ResponsiveImg)`
  width: 80px;
  &.banner {
    width: auto;
    max-width: 300%;
  }
`;

type Props = {
  onSelect?: () => void;
  userSportProfile?: DefaultListing_userSportProfile | null;
  item: DefaultListing_shopItem;
  inDialog?: boolean;
};

export const DefaultListing = ({
  onSelect,
  userSportProfile,
  item,
  inDialog,
}: Props) => {
  const [showDialog, setShowDialog] = useState(false);

  if (useItemExpired(item.expirationDate)) {
    return null;
  }

  const {
    clubBadge = null,
    background = null,
    achievementBadges = [],
  } = userSportProfile ?? {};
  const itemEquipped = [
    clubBadge?.id,
    background?.id,
    ...(achievementBadges || []).map(badge => badge?.id),
  ].includes(item.id);

  const itemStatus = (
    <ClubShopItemStatus status={itemEquipped ? 'equipped' : 'owned'} />
  );

  return (
    <>
      <Item inDialog={inDialog} onClick={() => setShowDialog(true)}>
        <ImageContainer
          className={classnames({
            background: item.position === ShopItemType.BACKGROUND,
          })}
        >
          <ItemImage
            className={classnames({
              banner: item.position === ShopItemType.BANNER,
            })}
            cropWidth={320}
            src={item.pictureUrl}
            alt={item.name}
          />
        </ImageContainer>
        <FooterWrapper>
          <ClubShopItemFooter name={item.name} status={itemStatus} />
        </FooterWrapper>
      </Item>
      <ItemPreviewDialog
        open={showDialog}
        item={item}
        itemEquipped={itemEquipped}
        onSelect={onSelect}
        onClose={() => setShowDialog(false)}
      />
    </>
  );
};

DefaultListing.fragments = {
  shopItem: gql`
    fragment DefaultListing_shopItem on ClubShopItem {
      ... on ShopItemInterface {
        id
        createdAt
        name
        position
        pictureUrl
        price
        salePrice
        limitPerUser
        myLimitResetAt
        myPurchasesCount
        expirationDate
      }
      ...ItemPreviewDialog_shopItem
    }
    ${ItemPreviewDialog.fragments.shopItem}
  ` as TypedDocumentNode<DefaultListing_shopItem>,
  userSportProfile: gql`
    fragment DefaultListing_userSportProfile on UserSportProfileInterface {
      id
      clubBadge {
        id
      }
      background {
        id
      }
      achievementBadges {
        id
      }
    }
  ` as TypedDocumentNode<DefaultListing_userSportProfile>,
};
